// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useI18n } from "@/i18n"

/**
 * Documented contract gap (T12): the recipe detail data ui-ux-spec.md's
 * "Recipe and cooking" section calls for — full ingredient list, sourced
 * per-recipe allergen data, and ordered steps — has no REST endpoint or
 * mock fixture anywhere in `packages/contracts` (only aggregate
 * `MealInstance` fields and an Amy-tool-only `RecipeSearchResultSchema`
 * that the web app cannot call directly). Rather than fabricate ingredient
 * names, allergen claims, or cooking steps — which AGENTS.md forbids
 * outright for allergen/nutrition-adjacent data — these three panels state
 * the gap honestly instead of rendering placeholder content. The ADR-013
 * "always check product labels" disclaimer still always renders, since
 * it's static safety guidance, not data from this missing endpoint.
 */

export function RecipeIngredientsPanel() {
  const { t } = useI18n()
  return (
    <section
      aria-labelledby="recipe-ingredients-heading"
      className="rounded-bb-lg border border-line bg-paper p-4"
    >
      <h2 id="recipe-ingredients-heading" className="font-semibold text-ink">
        {t("views.recipe.ingredientsTitle")}
      </h2>
      <p className="mt-1 text-sm text-ink-2">{t("views.recipe.ingredientsGap")}</p>
    </section>
  )
}

export function RecipeAllergenPanel() {
  const { t } = useI18n()
  return (
    <section
      aria-labelledby="recipe-allergens-heading"
      className="rounded-bb-lg border border-line bg-paper p-4"
    >
      <h2 id="recipe-allergens-heading" className="font-semibold text-ink">
        {t("views.recipe.allergensTitle")}
      </h2>
      <p className="mt-1 text-sm text-ink-2">{t("views.recipe.allergensGap")}</p>
      <p className="mt-2 text-sm font-medium text-danger">{t("views.recipe.allergensDisclaimer")}</p>
    </section>
  )
}

export function RecipeStepsPanel() {
  const { t } = useI18n()
  return (
    <section
      aria-labelledby="recipe-steps-heading"
      className="rounded-bb-lg border border-line bg-paper p-4"
    >
      <h2 id="recipe-steps-heading" className="font-semibold text-ink">
        {t("views.recipe.stepsTitle")}
      </h2>
      <p className="mt-1 text-sm text-ink-2">{t("views.recipe.stepsGap")}</p>
    </section>
  )
}
